import { useState, useCallback } from 'react';
import { notify } from '../../../utils/notifications';
import api from '../../../api/api';
import DraggableList from '../shared/DraggableList';
import EntityPicker from '../shared/EntityPicker';
import PromptEditor from '../shared/PromptEditor';
import ConfirmDialog from '../shared/ConfirmDialog';
import '../WorkflowEditor.css';

/** Known variables resolved by the backend at generation time. */
const KNOWN_VARIABLES = ['verified_facts', 'timeline_summary'];

function DragHandle({ listeners }) {
  return (
    <div className="wfe-drag-handle" {...listeners}>
      <div className="wfe-drag-dots">
        <span /><span /><span /><span /><span /><span />
      </div>
    </div>
  );
}

function TemplateCard({ template, index, dispatch, allConfigs, dragListeners }) {
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editActive, setEditActive] = useState(true);
  const [editSystemPrompt, setEditSystemPrompt] = useState('');
  const [editGenPrompt, setEditGenPrompt] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const usedByCount = allConfigs.filter((c) =>
    (c.document_refs || []).includes(template?.id)
  ).length;

  const startEdit = () => {
    if (!template) return;
    setEditName(template.name || '');
    setEditActive(template.active !== false);
    setEditSystemPrompt(template.system_prompt || '');
    setEditGenPrompt(template.generation_prompt || '');
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const resp = await api.put(`/admin/document-templates/${template.id}`, {
        name: editName,
        active: editActive,
        system_prompt: editSystemPrompt,
        generation_prompt: editGenPrompt,
      });
      dispatch({ type: 'UPDATE_TEMPLATE', template: resp.data });
      notify.success('Template updated');
      setEditing(false);
    } catch {
      notify.error('Failed to update template');
    } finally {
      setSaving(false);
    }
  };

  if (!template) {
    return (
      <div className="wfe-template-card">
        <div className="wfe-template-header">
          <DragHandle listeners={dragListeners} />
          <span className="wfe-field-name" style={{ color: '#DC2626' }}>Missing template reference</span>
          <div className="wfe-field-actions-visible">
            <button
              className="wfe-field-btn danger"
              onClick={() => dispatch({ type: 'REMOVE_TEMPLATE_REF', index })}
            >
              Remove
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wfe-template-card">
      <div className="wfe-template-header">
        <DragHandle listeners={dragListeners} />
        <span className="wfe-template-name">{template.name}</span>
        <span className={`wfe-template-badge ${template.active !== false ? 'active' : 'inactive'}`}>
          {template.active !== false ? 'Active' : 'Inactive'}
        </span>
        <div className="wfe-template-actions">
          {editing ? (
            <button className="wfe-field-btn" style={{ color: '#0D9488', fontWeight: 500 }}>Editing</button>
          ) : (
            <button className="wfe-field-btn" onClick={startEdit}>Edit</button>
          )}
          <button
            className="wfe-field-btn danger"
            onClick={() => setConfirmRemove(true)}
          >
            Remove
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmRemove}
        title={`Remove "${template.name}" template?`}
        message="This removes the template from this config. The template definition will still exist and can be re-added later."
        confirmLabel="Remove"
        onConfirm={() => { setConfirmRemove(false); dispatch({ type: 'REMOVE_TEMPLATE_REF', index }); }}
        onCancel={() => setConfirmRemove(false)}
      />

      {editing && (
        <div className="wfe-template-body">
          {usedByCount > 1 && (
            <div className="wfe-shared-warning">
              <span>&#9888;</span>
              <span>
                This template is used by <strong>{usedByCount} facility configs</strong>. Changes will affect all of them.
              </span>
            </div>
          )}

          {/* Name + Active */}
          <div className="wfe-form-row">
            <div className="wfe-form-group">
              <label className="wfe-form-label">Template Name</label>
              <input className="wfe-input" value={editName} onChange={(e) => setEditName(e.target.value)} />
            </div>
            <div className="wfe-form-group">
              <label className="wfe-form-label">Status</label>
              <div className="wfe-toggle-row" style={{ marginTop: 8 }}>
                <button
                  className={`wfe-toggle ${editActive ? 'on' : ''}`}
                  onClick={() => setEditActive(!editActive)}
                  type="button"
                />
                <span className="wfe-toggle-label">
                  {editActive ? 'Active' : 'Inactive'} &mdash; {editActive ? 'included in' : 'excluded from'} document generation
                </span>
              </div>
            </div>
          </div>

          {/* System Prompt */}
          <div style={{ marginBottom: 20 }}>
            <PromptEditor
              label="System Prompt"
              helpText="sets the LLM's role and behavior"
              value={editSystemPrompt}
              onChange={setEditSystemPrompt}
              rows={4}
              showStats={true}
            />
          </div>

          {/* Generation Prompt */}
          <div style={{ marginBottom: 20 }}>
            <PromptEditor
              label="Generation Prompt"
              helpText="defines sections, structure, and instructions"
              value={editGenPrompt}
              onChange={setEditGenPrompt}
              rows={12}
              variables={KNOWN_VARIABLES}
              showStats={true}
            />
          </div>

          {/* Available Variables (read-only) */}
          <div className="wfe-form-group" style={{ marginBottom: 0 }}>
            <label className="wfe-form-label">
              Available Variables
              <span className="wfe-form-hint"> &mdash; supported placeholders you can use in the prompt above</span>
            </label>
            <div className="wfe-var-list">
              {KNOWN_VARIABLES.map((v) => (
                <span key={v} className="wfe-var-chip">{`{${v}}`}</span>
              ))}
            </div>
            <p className="wfe-var-hint">
              These variables are resolved by the system at generation time. Contact your developer to add new ones.
            </p>
          </div>

          <div className="wfe-inline-edit-footer" style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #F0F0EA' }}>
            <button className="wfe-btn-sm primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Template'}
            </button>
            <button className="wfe-btn-sm ghost" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DocumentTemplatesSection({ config, allTemplates, allConfigs, dispatch }) {
  const [showPicker, setShowPicker] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ name: '', system_prompt: '', generation_prompt: '' });
  const [creating, setCreating] = useState(false);

  const templateRefs = config.document_refs || [];
  const templates = templateRefs.map((id) => allTemplates.find((t) => t.id === id));

  const handleCreateTemplate = async () => {
    if (!newTemplate.name.trim() || !newTemplate.generation_prompt.trim()) return;
    setCreating(true);
    try {
      const id = newTemplate.name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') + '_' + config.id;
      const resp = await api.post('/admin/document-templates', {
        id,
        name: newTemplate.name.trim(),
        system_prompt: newTemplate.system_prompt.trim(),
        generation_prompt: newTemplate.generation_prompt.trim(),
        variables: KNOWN_VARIABLES,
        active: true,
      });
      dispatch({ type: 'ADD_TEMPLATE', template: resp.data });
      dispatch({ type: 'ADD_TEMPLATE_REF', templateId: resp.data.id });
      setNewTemplate({ name: '', system_prompt: '', generation_prompt: '' });
      setShowCreateForm(false);
      notify.success('Template created and added');
    } catch (err) {
      if (err.response?.status === 409) {
        notify.error('A template with this ID already exists');
      } else {
        notify.error('Failed to create template');
      }
    } finally {
      setCreating(false);
    }
  };

  const getConfigTemplateRefs = useCallback((c) => c.document_refs || [], []);

  return (
    <div>
      <div className="wfe-section-header">
        <span className="wfe-section-label">Document Templates</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="wfe-btn-add" onClick={() => setShowCreateForm(true)}>
            + Create Template
          </button>
          <button className="wfe-btn-add" onClick={() => setShowPicker(true)}>
            + Add Existing
          </button>
        </div>
      </div>

      {showCreateForm && (
        <div className="wfe-section-card" style={{ marginBottom: 16 }}>
          <div className="wfe-section-title">New Document Template</div>
          <div className="wfe-form-group" style={{ marginBottom: 14 }}>
            <label className="wfe-form-label">Template Name</label>
            <input
              className="wfe-input"
              placeholder="e.g., Medical Necessity"
              value={newTemplate.name}
              onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
            />
          </div>
          <div style={{ marginBottom: 14 }}>
            <PromptEditor
              label="System Prompt"
              value={newTemplate.system_prompt}
              onChange={(v) => setNewTemplate({ ...newTemplate, system_prompt: v })}
              rows={3}
              placeholder="You are a medical documentation specialist..."
            />
          </div>
          <div style={{ marginBottom: 14 }}>
            <PromptEditor
              label="Generation Prompt"
              value={newTemplate.generation_prompt}
              onChange={(v) => setNewTemplate({ ...newTemplate, generation_prompt: v })}
              rows={8}
              variables={KNOWN_VARIABLES}
              placeholder="Generate a document using {verified_facts}..."
            />
          </div>
          <div className="wfe-inline-edit-footer">
            <button className="wfe-btn-sm primary" onClick={handleCreateTemplate} disabled={creating}>
              {creating ? 'Creating...' : 'Create & Add'}
            </button>
            <button className="wfe-btn-sm ghost" onClick={() => setShowCreateForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      {templateRefs.length === 0 && !showCreateForm ? (
        <div className="wfe-placeholder">
          <p>No document templates yet. Add or create a template to configure document generation.</p>
        </div>
      ) : (
        <DraggableList
          items={templateRefs}
          keyExtractor={(id) => id}
          onReorder={(newRefs) => dispatch({ type: 'REORDER_TEMPLATE_REFS', templateRefs: newRefs })}
          renderItem={(templateId, idx, { dragListeners }) => (
            <TemplateCard
              template={allTemplates.find((t) => t.id === templateId)}
              index={idx}
              dispatch={dispatch}
              allConfigs={allConfigs}
              dragListeners={dragListeners}
            />
          )}
        />
      )}

      <EntityPicker
        open={showPicker}
        onClose={() => setShowPicker(false)}
        title="Add Document Template"
        allEntities={allTemplates}
        excludeIds={templateRefs}
        getLabel={(t) => t.name}
        getPreview={(t) => t.generation_prompt ? `${t.generation_prompt.slice(0, 80)}...` : ''}
        onSelect={(id) => dispatch({ type: 'ADD_TEMPLATE_REF', templateId: id })}
        allConfigs={allConfigs}
        getConfigRefs={getConfigTemplateRefs}
      />
    </div>
  );
}
